import { watch, type Ref } from 'vue';
import type { IdentifySubscriberRequestDto } from '@peanuds/shared';
import { useAuth0 } from '@auth0/auth0-vue';
import i18next from 'i18next';

import { useNovuClient } from './useNovuClient';
import { useNotificationsStore } from '../store/notifications.store';

interface UseNotificationsInitializationOptions {
  /** Login status (e.g., from useAuthSessionStore). */
  isLoggedIn: Readonly<Ref<boolean>>;
  /** Current ID token; bootstrap derives the Novu organization context from it. */
  idToken: Readonly<Ref<string | null>>;
  /** Active organization from the ID token's `org_id` claim. */
  organizationId: Readonly<Ref<string | null>>;
  /** Auth0 context from useAuth0() (for accessing user data). */
  auth0: ReturnType<typeof useAuth0>;
}

/**
 * Manages the complete Novu initialization flow on login, token refresh and
 * organization switch. Switching organization tears the client down first so
 * the previous organization's feed is never shown while the new one loads.
 */
export const useNotificationsInitialization = ({
  isLoggedIn,
  idToken,
  organizationId,
  auth0,
}: UseNotificationsInitializationOptions) => {
  const notificationsStore = useNotificationsStore();
  const { initialize: initializeNovu, disconnect: disconnectNovu } = useNovuClient();

  watch(
    () => ({
      loggedIn: isLoggedIn.value,
      idToken: idToken.value,
      organizationId: organizationId.value,
      user: auth0.user?.value,
      isLoading: auth0.isLoading.value,
    }),
    async (state, previousState) => {
      if (!state.loggedIn) {
        disconnectNovu();
        return;
      }

      if (previousState && previousState.organizationId !== state.organizationId) {
        disconnectNovu();
      }

      if (state.isLoading || !state.user) {
        return;
      }

      const bootstrapResult = await initializeNovu();

      if (bootstrapResult !== 'subscriber-missing') {
        return;
      }

      const { sub, email, given_name, family_name, phone_number, picture } = state.user;

      if (!sub) {
        console.error('No subscriber ID found');
        return;
      }

      const subscriberData: IdentifySubscriberRequestDto = {
        subscriberId: sub,
        email,
        firstName: given_name,
        lastName: family_name,
        phone: phone_number,
        avatar: picture,
        locale: i18next.language || undefined,
      };

      try {
        const subscriber = await notificationsStore.identifySubscriber({ body: subscriberData });

        if (!subscriber) {
          return;
        }
      } catch (error) {
        console.error('Failed to identify subscriber:', error);
        return;
      }

      // Retry bootstrap after creating the subscriber server-side.
      void initializeNovu();
    },
    { immediate: true },
  );
};
